import React, { useState, useCallback, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import { parseFile } from '@/utils/loadProfileParser';
import { formatConsumption, formatPriceCt } from '@/utils/formatters';
import {
  FiUpload, FiFile, FiCheck, FiX, FiDownload, FiBarChart2,
  FiClock, FiZap, FiSun, FiMoon, FiCalendar, FiTrendingUp,
  FiArrowUp, FiArrowDown, FiRefreshCw, FiInfo
} from 'react-icons/fi';

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const LastgangAnalyse = () => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [chartView, setChartView] = useState('lastgang'); // lastgang, tageszeit, wochentag, heatmap, monat
  const inputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const processFile = async (selectedFile) => {
    setFile(selectedFile);
    setError(null);
    setIsProcessing(true);
    setResult(null);
    setAnalysis(null);

    try {
      const parsed = await parseFile(selectedFile);
      setResult(parsed);
      setAnalysis(buildAnalysis(parsed));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) processFile(e.target.files[0]);
  };

  const resetAnalysis = () => {
    setFile(null);
    setResult(null);
    setAnalysis(null);
    setError(null);
    setChartView('lastgang');
  };

  // ==========================================
  // ANALYSE-ENGINE
  // ==========================================
  const buildAnalysis = (parsed) => {
    const { data, totalKwh, peakKwh, offPeakKwh, dataPoints, intervalMinutes } = parsed;

    // Stundenwerte
    const hourly = Array(24).fill(null).map(() => ({ total: 0, count: 0 }));
    // Wochentage
    const weekday = Array(7).fill(null).map(() => ({ total: 0, count: 0, peak: 0, offPeak: 0 }));
    // Monatswerte
    const monthly = {};
    // Heatmap: Wochentag x Stunde
    const heatmap = Array(7).fill(null).map(() => Array(24).fill(null).map(() => ({ total: 0, count: 0 })));
    // Min/Max Tracking
    let maxValue = 0;
    let maxTime = null;
    let minValue = Infinity;
    let minTime = null;
    // Tageswerte
    const daily = {};

    data.forEach(d => {
      const hour = d.time.getHours();
      const day = d.time.getDay();
      const monthKey = `${d.time.getFullYear()}-${String(d.time.getMonth() + 1).padStart(2, '0')}`;
      const dayKey = d.time.toISOString().slice(0, 10);

      hourly[hour].total += d.value;
      hourly[hour].count++;

      weekday[day].total += d.value;
      weekday[day].count++;
      if (hour >= 8 && hour < 20) weekday[day].peak += d.value;
      else weekday[day].offPeak += d.value;

      heatmap[day][hour].total += d.value;
      heatmap[day][hour].count++;

      if (!monthly[monthKey]) monthly[monthKey] = { total: 0, count: 0, peak: 0, offPeak: 0 };
      monthly[monthKey].total += d.value;
      monthly[monthKey].count++;
      if (hour >= 8 && hour < 20) monthly[monthKey].peak += d.value;
      else monthly[monthKey].offPeak += d.value;

      if (!daily[dayKey]) daily[dayKey] = { total: 0, max: 0, min: Infinity };
      daily[dayKey].total += d.value;
      daily[dayKey].max = Math.max(daily[dayKey].max, d.value);
      daily[dayKey].min = Math.min(daily[dayKey].min, d.value);

      if (d.value > maxValue) { maxValue = d.value; maxTime = d.time; }
      if (d.value < minValue) { minValue = d.value; minTime = d.time; }
    });

    // Durchschnittswerte berechnen
    const hourlyAvg = hourly.map(h => h.count > 0 ? h.total / h.count : 0);
    const weekdayAvg = weekday.map(w => w.count > 0 ? w.total / w.count : 0);

    // Grundlast = Minimum der Durchschnittsstundenwerte
    const baseLoad = Math.min(...hourlyAvg.filter(v => v > 0));
    // Spitzenlast = Maximum
    const peakLoad = Math.max(...hourlyAvg);
    // Lastfaktor
    const loadFactor = peakLoad > 0 ? (hourlyAvg.reduce((s, v) => s + v, 0) / 24) / peakLoad : 0;

    // Wochenend-Anteil
    const weekendKwh = weekday[0].total + weekday[6].total;
    const weekdayKwh = totalKwh - weekendKwh;
    const weekendPercent = totalKwh > 0 ? (weekendKwh / totalKwh) * 100 : 0;

    // Nacht-Anteil (22-6 Uhr)
    const nightKwh = hourly.slice(0, 6).reduce((s, h) => s + h.total, 0) +
                     hourly.slice(22).reduce((s, h) => s + h.total, 0);
    const nightPercent = totalKwh > 0 ? (nightKwh / totalKwh) * 100 : 0;

    // Tagesanzahl
    const dayKeys = Object.keys(daily);
    const totalDays = dayKeys.length;

    // Durchschnittlicher Tagesverbrauch
    const avgDailyKwh = totalDays > 0 ? totalKwh / totalDays : 0;

    // Hochgerechneter Jahresverbrauch
    const annualKwh = avgDailyKwh * 365;

    return {
      totalKwh,
      peakKwh,
      offPeakKwh,
      peakPercent: totalKwh > 0 ? (peakKwh / totalKwh) * 100 : 0,
      dataPoints,
      intervalMinutes,
      hourlyAvg,
      weekdayData: weekday,
      weekdayAvg,
      monthly: Object.entries(monthly).sort(([a], [b]) => a.localeCompare(b)),
      heatmapData: heatmap,
      maxValue,
      maxTime,
      minValue: minValue === Infinity ? 0 : minValue,
      minTime,
      baseLoad,
      peakLoad,
      loadFactor,
      weekendPercent,
      nightPercent,
      totalDays,
      avgDailyKwh,
      annualKwh,
      daily,
      rawData: data,
    };
  };

  // ==========================================
  // CHART KONFIGURATIONEN
  // ==========================================

  const getLastgangChart = () => {
    if (!analysis) return {};
    const data = analysis.rawData;
    // Sample wenn zu viele Datenpunkte
    const step = data.length > 2000 ? Math.ceil(data.length / 2000) : 1;
    const sampled = data.filter((_, i) => i % step === 0);

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const p = params[0];
          const date = new Date(p.value[0]);
          return `<strong>${date.toLocaleString('de-DE')}</strong><br/>Verbrauch: ${p.value[1].toFixed(2)} kW`;
        }
      },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        { start: 0, end: 100, height: 25 }
      ],
      xAxis: {
        type: 'time',
        axisLabel: {
          formatter: (v) => new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
          color: '#6b7280'
        }
      },
      yAxis: {
        type: 'value',
        name: 'kW',
        axisLabel: { color: '#6b7280' },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [{
        name: 'Verbrauch',
        type: 'line',
        data: sampled.map(d => [d.time.getTime(), d.value]),
        smooth: false,
        symbol: 'none',
        lineStyle: { color: '#3b82f6', width: 1.5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(59,130,246,0.25)' },
              { offset: 1, color: 'rgba(59,130,246,0.02)' }
            ]
          }
        },
        markLine: {
          data: [
            { type: 'average', name: 'Durchschnitt', lineStyle: { color: '#6b7280', type: 'dashed' } },
            { yAxis: analysis.baseLoad, name: 'Grundlast', lineStyle: { color: '#10b981', type: 'dashed' } },
          ],
          label: { formatter: '{b}: {c} kW', fontSize: 10 }
        }
      }]
    };
  };

  const getTageszeitChart = () => {
    if (!analysis) return {};
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const p = params[0];
          return `<strong>${p.name}:00 Uhr</strong><br/>Durchschnitt: ${p.value.toFixed(2)} kW`;
        }
      },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}`),
        axisLabel: { color: '#6b7280' }
      },
      yAxis: {
        type: 'value',
        name: 'kW (Durchschnitt)',
        axisLabel: { color: '#6b7280' },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [{
        name: 'Durchschnitt',
        type: 'bar',
        data: analysis.hourlyAvg.map((v, i) => ({
          value: v,
          itemStyle: {
            color: (i >= 8 && i < 20) ? '#f59e0b' : '#3b82f6',
            borderRadius: [4, 4, 0, 0]
          }
        })),
      }],
      graphic: [
        {
          type: 'text',
          left: '15%', top: 10,
          style: { text: 'Peak (8-20h)', fill: '#f59e0b', fontSize: 11 }
        },
        {
          type: 'text',
          right: '15%', top: 10,
          style: { text: 'Off-Peak', fill: '#3b82f6', fontSize: 11 }
        }
      ]
    };
  };

  const getWochentagChart = () => {
    if (!analysis) return {};
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          let s = `<strong>${params[0].name}</strong><br/>`;
          params.forEach(p => { s += `${p.seriesName}: ${p.value.toFixed(1)} kWh<br/>`; });
          return s;
        }
      },
      legend: { data: ['Peak (8-20h)', 'Off-Peak'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '12%', containLabel: true },
      xAxis: {
        type: 'category',
        data: WEEKDAYS,
        axisLabel: { color: '#6b7280' }
      },
      yAxis: {
        type: 'value',
        name: 'kWh (Durchschnitt/Tag)',
        axisLabel: { color: '#6b7280' },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      series: [
        {
          name: 'Peak (8-20h)',
          type: 'bar',
          stack: 'total',
          data: analysis.weekdayData.map(w => w.count > 0 ? w.peak / (w.count / 24 * 12) : 0),
          itemStyle: { color: '#f59e0b', borderRadius: [0, 0, 0, 0] }
        },
        {
          name: 'Off-Peak',
          type: 'bar',
          stack: 'total',
          data: analysis.weekdayData.map(w => w.count > 0 ? w.offPeak / (w.count / 24 * 12) : 0),
          itemStyle: { color: '#3b82f6', borderRadius: [4, 4, 0, 0] }
        }
      ]
    };
  };

  const getHeatmapChart = () => {
    if (!analysis) return {};
    const heatData = [];
    analysis.heatmapData.forEach((day, dayIdx) => {
      day.forEach((hour, hourIdx) => {
        if (hour.count > 0) {
          heatData.push([hourIdx, dayIdx, +(hour.total / hour.count).toFixed(2)]);
        }
      });
    });

    const allValues = heatData.map(d => d[2]);
    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);

    return {
      tooltip: {
        formatter: (p) => `<strong>${WEEKDAYS[p.value[1]]}, ${String(p.value[0]).padStart(2, '0')}:00</strong><br/>Durchschnitt: ${p.value[2]} kW`
      },
      grid: { left: '12%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}h`),
        splitArea: { show: true },
        axisLabel: { color: '#6b7280', fontSize: 10 }
      },
      yAxis: {
        type: 'category',
        data: WEEKDAYS_SHORT,
        axisLabel: { color: '#6b7280' }
      },
      visualMap: {
        min: minVal, max: maxVal,
        calculable: true, orient: 'horizontal',
        left: 'center', bottom: '0%',
        inRange: { color: ['#dbeafe', '#3b82f6', '#1e40af', '#f59e0b', '#ef4444'] },
        textStyle: { color: '#6b7280' },
        formatter: (v) => `${v.toFixed(1)} kW`
      },
      series: [{
        type: 'heatmap',
        data: heatData,
        label: { show: false },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.3)' }
        }
      }]
    };
  };

  const getMonatChart = () => {
    if (!analysis || analysis.monthly.length === 0) return {};
    const labels = analysis.monthly.map(([k]) => {
      const [y, m] = k.split('-');
      const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
      return `${months[parseInt(m) - 1]} ${y}`;
    });

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          let s = `<strong>${params[0].name}</strong><br/>`;
          params.forEach(p => { s += `${p.seriesName}: ${Math.round(p.value).toLocaleString('de-DE')} kWh<br/>`; });
          return s;
        }
      },
      legend: { data: ['Peak', 'Off-Peak'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '12%', containLabel: true },
      xAxis: { type: 'category', data: labels, axisLabel: { color: '#6b7280' } },
      yAxis: { type: 'value', name: 'kWh', axisLabel: { color: '#6b7280' }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series: [
        {
          name: 'Peak',
          type: 'bar',
          stack: 'total',
          data: analysis.monthly.map(([, v]) => v.peak),
          itemStyle: { color: '#f59e0b' }
        },
        {
          name: 'Off-Peak',
          type: 'bar',
          stack: 'total',
          data: analysis.monthly.map(([, v]) => v.offPeak),
          itemStyle: { color: '#3b82f6', borderRadius: [4, 4, 0, 0] }
        }
      ]
    };
  };

  const chartOptions = {
    lastgang: { label: 'Lastgang', icon: FiZap, getChart: getLastgangChart },
    tageszeit: { label: 'Tageszeit', icon: FiClock, getChart: getTageszeitChart },
    wochentag: { label: 'Wochentag', icon: FiCalendar, getChart: getWochentagChart },
    heatmap: { label: 'Heatmap', icon: FiBarChart2, getChart: getHeatmapChart },
    monat: { label: 'Monat', icon: FiTrendingUp, getChart: getMonatChart },
  };

  // ==========================================
  // CSV EXPORT
  // ==========================================
  const exportCSV = () => {
    if (!analysis) return;
    const lines = [
      ['Kennzahl', 'Wert', 'Einheit'].join(';'),
      ['Gesamtverbrauch', analysis.totalKwh.toFixed(2), 'kWh'].join(';'),
      ['Hochgerechneter Jahresverbrauch', Math.round(analysis.annualKwh), 'kWh'].join(';'),
      ['Analysezeitraum', analysis.totalDays, 'Tage'].join(';'),
      ['Datenpunkte', analysis.dataPoints, ''].join(';'),
      ['Messintervall', analysis.intervalMinutes, 'Minuten'].join(';'),
      ['Durchschnitt pro Tag', analysis.avgDailyKwh.toFixed(1), 'kWh'].join(';'),
      ['Grundlast', analysis.baseLoad.toFixed(2), 'kW'].join(';'),
      ['Spitzenlast', analysis.peakLoad.toFixed(2), 'kW'].join(';'),
      ['Lastfaktor', (analysis.loadFactor * 100).toFixed(1), '%'].join(';'),
      ['Peak-Anteil (8-20h)', analysis.peakPercent.toFixed(1), '%'].join(';'),
      ['Nacht-Anteil (22-6h)', analysis.nightPercent.toFixed(1), '%'].join(';'),
      ['Wochenend-Anteil', analysis.weekendPercent.toFixed(1), '%'].join(';'),
      ['Maximum', analysis.maxValue.toFixed(2), 'kW'].join(';'),
      ['Maximum Zeitpunkt', analysis.maxTime?.toLocaleString('de-DE'), ''].join(';'),
      ['Minimum', analysis.minValue.toFixed(2), 'kW'].join(';'),
      ['Minimum Zeitpunkt', analysis.minTime?.toLocaleString('de-DE'), ''].join(';'),
      '',
      ['Stunde', 'Durchschnitt kW'].join(';'),
      ...analysis.hourlyAvg.map((v, i) => [`${String(i).padStart(2, '0')}:00`, v.toFixed(3)].join(';')),
      '',
      ['Wochentag', 'Durchschnitt kWh', 'Peak kWh', 'Off-Peak kWh'].join(';'),
      ...analysis.weekdayData.map((w, i) => [
        WEEKDAYS[i],
        w.count > 0 ? (w.total / (w.count / 96)).toFixed(1) : '0',
        w.count > 0 ? (w.peak / (w.count / 96)).toFixed(1) : '0',
        w.count > 0 ? (w.offPeak / (w.count / 96)).toFixed(1) : '0',
      ].join(';')),
    ];

    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lastgang-analyse-${file?.name?.replace(/\.[^.]+$/, '') || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lastgang-Analyse</h1>
          <p className="text-gray-500 mt-1">Verbrauchsprofil hochladen und sofort analysieren</p>
        </div>
        {result && (
          <div className="flex items-center space-x-2">
            <Button variant="secondary" icon={FiDownload} onClick={exportCSV}>
              Export CSV
            </Button>
            <Button variant="ghost" icon={FiRefreshCw} onClick={resetAnalysis}>
              Neue Datei
            </Button>
          </div>
        )}
      </div>

      {/* Upload-Bereich */}
      {!result && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`
                relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all
                ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'}
                ${isProcessing ? 'pointer-events-none opacity-60' : ''}
              `}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              {isProcessing ? (
                <div className="space-y-3">
                  <div className="animate-spin mx-auto w-10 h-10 border-2 border-primary-600 border-t-transparent rounded-full" />
                  <p className="text-gray-600 font-medium">Datei wird analysiert...</p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FiUpload className="text-primary-600" size={28} />
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    Lastgang-Datei hochladen
                  </p>
                  <p className="text-gray-500 mt-2">
                    CSV oder Excel mit Zeitstempel und Verbrauchswerten
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    Unterstuetzte Formate: .csv, .xlsx, .xls — 15-Min oder Stunden-Intervall
                  </p>
                </>
              )}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
              >
                <FiX className="text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-800 font-medium">Fehler beim Analysieren</p>
                  <p className="text-red-600 text-sm mt-1">{error}</p>
                </div>
              </motion.div>
            )}
          </Card>
        </motion.div>
      )}

      {/* Ergebnis-Bereich */}
      {result && analysis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Datei-Info */}
          <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <FiCheck className="text-green-600" size={20} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800 truncate">
                {file?.name} — {analysis.dataPoints.toLocaleString('de-DE')} Datenpunkte analysiert
              </p>
              <p className="text-xs text-green-600">
                {analysis.totalDays} Tage &middot; {analysis.intervalMinutes}-Minuten-Intervall
              </p>
            </div>
          </div>

          {/* KPI-Karten */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <KPICard
              icon={FiZap} color="blue"
              label="Gesamtverbrauch"
              value={`${Math.round(analysis.totalKwh).toLocaleString('de-DE')} kWh`}
            />
            <KPICard
              icon={FiCalendar} color="indigo"
              label="Hochgerechnet/Jahr"
              value={`${Math.round(analysis.annualKwh).toLocaleString('de-DE')} kWh`}
            />
            <KPICard
              icon={FiArrowUp} color="red"
              label="Spitzenlast"
              value={`${analysis.peakLoad.toFixed(1)} kW`}
              sub={analysis.maxTime ? `${analysis.maxTime.toLocaleString('de-DE', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
            />
            <KPICard
              icon={FiArrowDown} color="green"
              label="Grundlast"
              value={`${analysis.baseLoad.toFixed(1)} kW`}
            />
            <KPICard
              icon={FiSun} color="amber"
              label="Peak-Anteil"
              value={`${analysis.peakPercent.toFixed(0)}%`}
              sub="8:00 - 20:00 Uhr"
            />
            <KPICard
              icon={FiMoon} color="purple"
              label="Nacht-Anteil"
              value={`${analysis.nightPercent.toFixed(0)}%`}
              sub="22:00 - 6:00 Uhr"
            />
          </div>

          {/* Detailkarten */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <div className="flex items-center space-x-2 mb-3">
                <FiTrendingUp className="text-primary-600" />
                <span className="font-semibold text-gray-900">Lastfaktor</span>
              </div>
              <div className="flex items-end space-x-2">
                <span className="text-3xl font-bold text-gray-900">{(analysis.loadFactor * 100).toFixed(0)}%</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {analysis.loadFactor > 0.6 ? 'Gleichmaessiger Verbrauch — gut fuer Grundlasttarife' :
                 analysis.loadFactor > 0.4 ? 'Mittlerer Lastfaktor — Optimierungspotenzial vorhanden' :
                 'Hohe Lastspitzen — Lastmanagement empfohlen'}
              </p>
              <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    analysis.loadFactor > 0.6 ? 'bg-green-500' :
                    analysis.loadFactor > 0.4 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${analysis.loadFactor * 100}%` }}
                />
              </div>
            </Card>

            <Card>
              <div className="flex items-center space-x-2 mb-3">
                <FiBarChart2 className="text-primary-600" />
                <span className="font-semibold text-gray-900">Tagesverbrauch</span>
              </div>
              <div className="flex items-end space-x-2">
                <span className="text-3xl font-bold text-gray-900">
                  {analysis.avgDailyKwh.toFixed(0)}
                </span>
                <span className="text-gray-500 text-sm mb-1">kWh/Tag</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Wochenend-Anteil: {analysis.weekendPercent.toFixed(0)}%
                {analysis.weekendPercent < 15 ? ' — typisch fuer Buerogebaeude' :
                 analysis.weekendPercent > 35 ? ' — durchgehender Betrieb' : ''}
              </p>
            </Card>

            <Card>
              <div className="flex items-center space-x-2 mb-3">
                <FiInfo className="text-primary-600" />
                <span className="font-semibold text-gray-900">Optimierungspotenzial</span>
              </div>
              <div className="space-y-2 text-sm">
                {analysis.peakPercent > 60 && (
                  <div className="flex items-start space-x-2">
                    <FiCheck className="text-green-500 mt-0.5 flex-shrink-0" size={14} />
                    <span className="text-gray-700">Hoher Peak-Anteil — Lastverschiebung moeglich</span>
                  </div>
                )}
                {analysis.loadFactor < 0.5 && (
                  <div className="flex items-start space-x-2">
                    <FiCheck className="text-green-500 mt-0.5 flex-shrink-0" size={14} />
                    <span className="text-gray-700">Lastspitzen glaetten — bis 10% Ersparnis</span>
                  </div>
                )}
                {analysis.nightPercent > 20 && (
                  <div className="flex items-start space-x-2">
                    <FiCheck className="text-green-500 mt-0.5 flex-shrink-0" size={14} />
                    <span className="text-gray-700">Nachtverbrauch nutzbar fuer guenstige Tarife</span>
                  </div>
                )}
                {analysis.weekendPercent > 25 && (
                  <div className="flex items-start space-x-2">
                    <FiCheck className="text-green-500 mt-0.5 flex-shrink-0" size={14} />
                    <span className="text-gray-700">Wochenend-Betrieb — flexible Tarife vorteilhaft</span>
                  </div>
                )}
                <div className="flex items-start space-x-2">
                  <FiCheck className="text-green-500 mt-0.5 flex-shrink-0" size={14} />
                  <span className="text-gray-700">
                    Individuelles Beschaffungsprofil — bis zu 15% Ersparnis ggue. Festpreis
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Chart-Bereich */}
          <Card>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <Card.Title>Verbrauchsanalyse</Card.Title>
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                {Object.entries(chartOptions).map(([key, opt]) => (
                  <button
                    key={key}
                    onClick={() => setChartView(key)}
                    className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      chartView === key
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <opt.icon size={14} />
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="h-96">
              <ReactECharts
                option={chartOptions[chartView].getChart()}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

// KPI-Karten Komponente
const KPICard = ({ icon: Icon, color, label, value, sub }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    red: 'bg-red-100 text-red-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <Card className="!p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]} mb-2`}>
        <Icon size={16} />
      </div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </Card>
  );
};

export default LastgangAnalyse;
